import {useState, useMemo, useEffect, forwardRef, useImperativeHandle} from "react";
import ForecastTable from "../components/ForecastTable";
import { Col, Row, useIsMobile } from "../components/Flexbox";
import Text from "../components/Text";
import * as XLSX from 'xlsx';
import * as tf from '@tensorflow/tfjs';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Button } from "@mui/material";
import {setWeek, setYear} from 'date-fns';
import {es} from 'date-fns/locale';
import { strMonthMap, monthMap } from "../utils";
import { fetchAuthSession } from 'aws-amplify/auth';
const ForecatNormalizer = forwardRef((props, ref) => {

    const isMobile = useIsMobile();
    const [fileIndex, setFileIndex] = useState(0);
    const [sheetIndex, setSheetIndex] = useState(0);
    const [tableIndex, setTableIndex] = useState(0);
    const [dataFrames, setDataFrames] = useState({});

    const fileNames = useMemo(() => Object.keys(dataFrames || {}), [dataFrames]);
    const fileName = fileNames[fileIndex] || null;

    const [fprData, setFprData] = useState({});

    const sheetNames = useMemo(() => (
    fileName && dataFrames[fileName]
        ? Object.keys(dataFrames[fileName])
        : []
    ), [dataFrames, fileName]);

    const sheetName = sheetNames[sheetIndex] || null;

    const tables = useMemo(() => (
        fileName && sheetName && dataFrames[fileName]?.[sheetName]
            ? dataFrames[fileName][sheetName]
            : []
    ), [dataFrames, fileName, sheetName]);
    // console.log("Table: ", tables);

    const table = tables[tableIndex] || null;
    // console.log("Other table: ", table);

    const [predictedIdColumn, setPredictedIdColumn] = useState({name: '', index: -1});
    const [predictedMonthColumns, setPredictedMonthColumns] = useState({names: [], indexes: []});
    const [predictedWeekColumns, setPredictedWeekColumns] = useState({names: [], indexes: []});
    const [allPredictions, setAllPredictions] = useState({});
    const [loadingColumnIdentification, setLoadingColumnIdentification] = useState(false);
    const [message, setMessage] = useState({});
    // State to track valid columns per table
    const [validColumnsMap, setValidColumnsMap] = useState({});
    const [overwrites, setOverwrites] = useState({});
    const [ignoredColumns, setIgnoredColumns] = useState({})

    /* REGEX validations */
    // Month regex (both Spanish and English full and abbreviated)
    const monthRegex = /(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre|jan|ene|feb|mar|apr|abr|may|jun|jul|aug|ago|sep|oct|nov|dec|dic)/i;
    //Checks for numbers from 0 to 53 (Following ISO 8601), allowing a leading zero (e.g. "05")
    const weekRegex = /(^(semana|week|wk|sem)\s*\b(0[0-9]|1?[0-9]|[1-4][0-9]|5[0-3])\b|^\s*(0[0-9]|1?[0-9]|[1-4][0-9]|5[0-3])\s*$)/i; //Checks if the headers include something like Week 52, Week 05, or just the number 52/05
    // Quantity-related keywords
    const keywordRegex = /(cantidad|required|requerido|stock pack|qty|cantidad requerida)/i;

    const normalizeMonthName = (monthHeader) => {
        for (const [key, normalizedName] of Object.entries(strMonthMap)) {
            if(monthHeader.toLowerCase().includes(key)) {
                return normalizedName;
            }
        }

        return monthHeader;
    }

// Helper to get current context key
    const getContextKey = () => `${fileName}|${sheetName}|${tableIndex}`;
    // #### Navigation Functions ####
    // Change file index based on navigation
    const changeFile = (dir) => {
        setPredictedMonthColumns({ names: [], indexes: [] });
        setPredictedWeekColumns({ names: [], indexes: [] });
        const newIndex = fileIndex + dir;
        if (newIndex >= 0 && newIndex < fileNames.length) {
            setFileIndex(newIndex);
            setSheetIndex(0);
            setTableIndex(0);
        }
    };

    // Change sheet index based on navigation
    const changeSheet = (dir) => {
        const newIndex = sheetIndex + dir;
        if (newIndex >= 0 && newIndex < sheetNames.length) {
            setSheetIndex(newIndex);
            setTableIndex(0);
        }
    };

    // Change table index based on navigation
    const changeTable = (dir) => {
        const newIndex = tableIndex + dir;
        if (newIndex >= 0 && newIndex < tables.length) {
            setTableIndex(newIndex);
        }
    };

    // #### Data Processing Functions ####
    // Remove empty columns from a table
    const removeEmptyColumns = (table) => {
        const transposed = table[0].map((_, colIndex) =>
            table.map(row => row[colIndex])
        );

        const keepIndexes = transposed
            .map(col => col.some(cell => cell !== null && cell !== ''))
            .map((keep, index) => (keep ? index : null))
            .filter(index => index !== null);

        return table.map(row => keepIndexes.map(i => row[i]));
    }

    // Extract 2D tables from a matrix
    const extractTables2D = (matrix) => {
        const numRows = matrix.length;
        const numCols = Math.max(...matrix.map(row => (row ? row.length : 0)));
        const visited = Array.from({ length: numRows }, () => Array(numCols).fill(false));
        const tables = [];

        const isEmptyCell = (r, c) => {
            const row = matrix[r];
            if (!row || typeof row[c] === 'undefined') return true;
            return row[c] === null || row[c] === '';
        }

        const bfs = (startR, startC) => {
            const queue = [[startR, startC]];
            let top = startR, bottom = startR, left = startC, right = startC;

            visited[startR][startC] = true;

            while (queue.length > 0) {
            const [r, c] = queue.shift();

            // Update bounds
            top = Math.min(top, r);
            bottom = Math.max(bottom, r);
            left = Math.min(left, c);
            right = Math.max(right, c);

            const directions = [
                [r + 1, c],
                [r - 1, c],
                [r, c + 1],
                [r, c - 1],
            ];

            for (const [nr, nc] of directions) {
                if (
                nr >= 0 && nr < numRows &&
                nc >= 0 && nc < numCols &&
                !visited[nr][nc] &&
                !isEmptyCell(nr, nc)
                ) {
                visited[nr][nc] = true;
                queue.push([nr, nc]);
                }
            }
            }

            // Extract the submatrix for the table
            const block = [];
            for (let i = top; i <= bottom; i++) {
            const row = [];
            for (let j = left; j <= right; j++) {
                row.push((matrix[i] && matrix[i][j]) ?? null);
            }
            block.push(row);
            }

            return block;
        }

        for (let r = 0; r < numRows; r++) {
            for (let c = 0; c < numCols; c++) {
            if (!visited[r][c] && !isEmptyCell(r, c)) {
                const table = bfs(r, c);
                tables.push(table);
            }
            }
        }

        return tables;
    }


    useEffect(() => {
        props.selectedFiles.forEach(file => {
            
            const fileName = file.name.split('.')[0];

            if (dataFrames[fileName]) { //If the file already exists, skip it
                return;
            }

            const reader = new FileReader();

            reader.onload = (e) => {

                const binaryStr = e.target.result;
                const workbook = XLSX.read(binaryStr, { type: 'binary' });
                const fileName = file.name.split('.')[0];
                const fileResult  = {};
                // console.log("Workbook", workbook)
                workbook.SheetNames.forEach((sheetName, index) => {
                    // console.log(index)
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                        header: 1,
                        raw: false,
                        defval: null,
                    });

                    const tableBlocks = extractTables2D(jsonData);
                    
                    // console.log("Data: ", jsonData);
                    const parsedTables = tableBlocks.map(table => {
                        const cleaned = removeEmptyColumns(table);
                        if (cleaned.length < 2) return null; // no data
                        const headers = cleaned[0];
                        const data = cleaned.slice(1);
            
                        return data.map(row => {
                          const rowMap = new Map();
                          headers.forEach((h, i) => {
                            rowMap.set(h || `Unnamed_${i}`, row[i]);
                          });
                          return rowMap;
                        });
                      }).filter(Boolean); // Remove nulls
            
                    fileResult[sheetName] = parsedTables;
                });
                setDataFrames(prev => ({ ...prev, [fileName] : fileResult }));
            }

            reader.readAsBinaryString(file);
        });
    }, [props.selectedFiles])

    useEffect(() => {
        //Get the file names
        const currentFiles = props.selectedFiles.map(file => file.name.split('.')[0]);
        setDataFrames(prev => {
            const newData = {...prev};
            let changes = false;
            //Iterate through the data in ForecastNormalizer
            Object.keys(newData).forEach(name => {
                //If there isn't a file name in CrearForecast, delete it
                if (!currentFiles.includes(name)) {
                    delete newData[name];
                    changes = true;
                }
            });
            //Update the state
            return changes ? newData : prev;
        })
    }, [props.selectedFiles])

    // #### data clean up section ####
    // Delete a file
    const deleteFile = (fileNameToDelete) => {
        //Search for the position of the file in CrearForecast's list
        const parentIndex = props.selectedFiles.findIndex(f => f.name.split('.')[0] === fileNameToDelete);
        
        //If there's a match, remove the file, which changes the state, triggering the useEffect to clean the states
        if (parentIndex !== -1) {
            props.removeFile(parentIndex);
        }

        setPredictedMonthColumns({ names: [], indexes: [] });
        setPredictedWeekColumns({ names: [], indexes: [] });
        setDataFrames(prev => {
            const newData = { ...prev };
            delete newData[fileNameToDelete];
            return newData;
        });
        setFileIndex(fileIndex > 0 ? fileIndex - 1 : 0);
        setSheetIndex(0);
        setTableIndex(0);
    };

    // Delete a sheet
    const deleteSheet = (fileName, sheetNameToDelete) => {
        setDataFrames(prev => {
            const newData = { ...prev };
            if (newData[fileName]) {
                newData[fileName] = { ...newData[fileName] };
                delete newData[fileName][sheetNameToDelete];
                // Remove file entirely if no sheets left
                if (Object.keys(newData[fileName]).length === 0) {
                delete newData[fileName];
                }
            }
            return newData;
        });
        setSheetIndex(sheetIndex > 0 ? sheetIndex - 1 : 0);
        setTableIndex(0);
    };

    // Delete a table
    const deleteTable = (fileName, sheetName, tableIdx) => {

        setDataFrames(prev => {
            const newData = { ...prev };
            if (newData[fileName]?.[sheetName]) {
                newData[fileName] = { ...newData[fileName] };
                newData[fileName][sheetName] = newData[fileName][sheetName].filter((_, i) => i !== tableIdx);
                // Remove sheet if no tables left
                if (newData[fileName][sheetName].length === 0) {
                    delete newData[fileName][sheetName];
                    setSheetIndex(sheetIndex > 0 ? sheetIndex - 1 : 0);
                }
                // Remove file if no sheets left
                if (Object.keys(newData[fileName]).length === 0) {
                    delete newData[fileName];
                    setFileIndex(fileIndex > 0 ? fileIndex - 1 : 0);
                }
            }
            return newData;
        });
        setTableIndex(tableIndex > 0 ? tableIndex - 1 : 0);
    };

    // Delete a row from the current table
    const deleteRow = (rowIdxToDelete) => {
        if (!table) return;
        const newTables = [...tables];
        newTables[tableIndex] = newTables[tableIndex].filter((_, i) => i !== rowIdxToDelete);
      
        setDataFrames(prev => ({
          ...prev,
          [fileName]: {
            ...prev[fileName],
            [sheetName]: newTables,
          }
        }));
      };

    // Delete a column from the current table
    const deleteColumn = (colKeyToDelete) => {
        if (!table) return;
        const newTables = [...tables];
        
        newTables[tableIndex] = newTables[tableIndex].map(row => {
            const newRow = new Map(row);
            newRow.delete(colKeyToDelete);
            return newRow;
        });
        
        setDataFrames(prev => ({
            ...prev,
            [fileName]: {
            ...prev[fileName],
            [sheetName]: newTables,
            }
        }));
    };

    // Promote the first row to header and remove it from data
    const deleteHeaderRow = () => {
        const fileName = fileNames[fileIndex];
        const sheetName = sheetNames[sheetIndex];
    
        const table = dataFrames[fileName][sheetName][tableIndex];
        if (!table || table.length <= 1) return; // nothing to promote
        const newHeader = Array.from(table[0].values());
        const newTable = table.slice(1).map(row => {
            const newRow = new Map();
            Array.from(row.values()).forEach((col, i) => {
                newRow.set(newHeader[i], col);
            })
            return newRow;
        });

        setDataFrames(prev => {
            const updated = { ...prev };
            updated[fileName] = { ...updated[fileName] };
            updated[fileName][sheetName] = [...updated[fileName][sheetName]];
            updated[fileName][sheetName][tableIndex] = newTable;
            return updated;
        });
    };

    const handleOverwrite = (colName, type, offset = 0) => {
        const key = getContextKey();
        let isValid = false;
        
        /* Check if the type matches its name */
        if (type === 'id') {
            isValid = true; //The user can mark ANY column as ID
        } else if (type === 'month') {
            isValid = monthRegex.test(colName);
        } else if (type === 'week') {
            isValid = weekRegex.test(colName)
        }

        if(!isValid) {  
            setMessage(prev => ({
                ...prev, [colName]: {message: "Formato inválido", isValid: false}
            }));
            return;
        }

        setMessage(prev => ({
            ...prev, [colName]: {message: "Formato válido", isValid: true}
        }));

        setOverwrites(prev => {
            const currentOverwrite = prev[key] || {};
            const newOverwrite = {...currentOverwrite};

            newOverwrite[colName]={type, offset};   //Overwrites the entry 
            
            return {...prev, [key]: newOverwrite};
        });

        //Removes the column from the ignored list (if it's in there) to process it
        setIgnoredColumns(prev => {
            const newIgnored = {...prev};
            const currentIgnored = new Set(newIgnored[key] || []);
            currentIgnored.delete(colName);
            newIgnored[key] = Array.from(currentIgnored);
            return newIgnored;
        });
    };

    //Mark a column to be ignored, this deletes any overwrite.
    const handleDeselection = (colName) => {
        const key = getContextKey();

        setOverwrites(prev => {
            const newOverwrite = { ...(prev[key] || {})};

            delete newOverwrite[colName];

            return {...prev, [key]: newOverwrite};
        });

        setIgnoredColumns(prev => {
            const newIgnored = {...prev};
            const currentIgnored = new Set(newIgnored[key] || []);
            currentIgnored.add(colName);
            newIgnored[key] = Array.from(currentIgnored);
            return newIgnored;
        });
    }

    // ### predictions ###

    const predictIdColumn = async (table, contextKey, overwrites, ignoredColumns) => {
        if (!table || table.length === 0) {
            return {name: '', index: -1};
        }
    
        const key = contextKey;
        const userOverwrites = overwrites[key] || {};
        const ignoredCols = new Set(ignoredColumns[key] || []);
        const colNames = Array.from(table[0].keys());

        //Prioritizes the user's selection
        const overwrittenIdCol = colNames.find(col => userOverwrites[col]?.type === 'id');
        if(overwrittenIdCol) {
            const index = colNames.indexOf(overwrittenIdCol);
            return {name: overwrittenIdCol, index}; //Avoid the automatic detection if an overwrite is made
        }

        const model = await tf.loadLayersModel('/models/id_identifier_model/model.json');
        const predictionCounts = new Array(colNames.length).fill(0);
    
        for (let colIndex = 0; colIndex < colNames.length; colIndex++) {
            const col = colNames[colIndex];

            if(ignoredCols.has(col)) {  //Skip ignored columns
                continue; 
            }
            
            for (let i = 0; i < Math.min(table.length, 20); i++) {
                const cellValue = (table[i].get(col) ?? "").toString();
                const features = [
                    cellValue.length,
                    (cellValue.match(/\d/g) || []).length,
                    (cellValue.match(/[a-zA-Z]/g) || []).length
                ];
            
                const inputTensor = tf.tensor2d([features], [1, 3]);
                const prediction = model.predict(inputTensor);
                const predictionArray = await prediction.array();
            
                if (predictionArray[0][0] > 0.6) {
                    predictionCounts[colIndex]++;
                }
            
                inputTensor.dispose();
                prediction.dispose();
            }
        }
        //console.log("counts:", predictionCounts)
        const maxIndex = predictionCounts.indexOf(Math.max(...predictionCounts));
        const predictedColumn = colNames[maxIndex];
    
        // console.log("Predicted ID Column:", predictedColumn);
        return{name: predictedColumn, index: maxIndex};
    };

    const detectMonthQuantityColumns = async (table, contextKey, overwrites, ignoredColumns) => {
        if (!table || table.length === 0) {
            return {months: {names: [], indexes: []}, weeks: {names: [], indexes: []}};
        }
    
        const key = contextKey;
        const userOverwrites = overwrites[key] || {};
        const ignoredCols = new Set(ignoredColumns[key] || []);
        const colNames = Array.from(table[0].keys());

        let detectedMonths = {names: [], indexes: [], offsets: {}};
        let detectedWeeks = {names: [], indexes: [], offsets: {}};

        /* Filter month and week overwrites */
        const overwrittenMonths = colNames.map((col, i) => ({
            name: col, index: i, overwrite: userOverwrites[col]?.type === 'month' ? userOverwrites[col]: null}))
            .filter(c => c.overwrite);
        
        const overwrittenWeeks = colNames.map((col, i) => ({
            name: col, index: i, overwrite: userOverwrites[col]?.type === 'week' ? userOverwrites[col]: null}))
            .filter(c => c.overwrite);
    
        const monthOffsets = {};
        let nextMonthYear = 0;
        let prevMonthIndex = -1;

        colNames.forEach(header => {
            if(userOverwrites[header]?.type === 'month') {
                monthOffsets[header] = userOverwrites[header].offset;
                return;
            }

            const strMatch = Object.entries(strMonthMap).find(([monthKey]) => header.toLowerCase().includes(monthKey));

            if(strMatch) {
                const [, normalizedName] = strMatch;
                const monthIndex = monthMap[normalizedName];

                if(prevMonthIndex !== -1 && monthIndex < prevMonthIndex) {
                    nextMonthYear++;
                }
                monthOffsets[header] = nextMonthYear;
                prevMonthIndex = monthIndex;
            }
        });

        const weekOffsets = {};
        let nextWeekYear = 0;
        let prevWeekNumber = -1;

        colNames.forEach(header => {

            if(userOverwrites[header]?.type === 'week') {
                weekOffsets[header] = userOverwrites[header].offset;
                return;
            }

            if(weekRegex.test(header)) {
                const weekNumber = parseInt(header.match(/\d+/)[0]);
                if(prevWeekNumber !== -1 && weekNumber < prevWeekNumber) {
                    nextWeekYear++;
                }
                weekOffsets[header] = nextWeekYear;
                prevWeekNumber = weekNumber;
            }
        });


        // Step 1: Look for month columns and/or week columns
        const monthMatches = colNames
            .map((col, i) => ({ name: col, index: i }))
            .filter(({ name }) => !userOverwrites[name] && !ignoredCols.has(name) && monthRegex.test(name.toLowerCase()));    //Filter overwritten and ignored cols by the user

        if (monthMatches.length > 0 || overwrittenMonths.length > 0) {
            const combinedMonths = [...overwrittenMonths.map(c => ({name: c.name, index: c.index})), ...monthMatches];

            detectedMonths = {
                names: combinedMonths.map(m => m.name),
                indexes: combinedMonths.map(m => m.index),
                offsets: monthOffsets
            };
            // console.log("Detected by month regex:", detectedMonths);
        }
        const weekMatches = colNames
            .map((col, i) => ({name: col, index: i}))
            .filter(({name}) => !userOverwrites[name] && !ignoredCols.has(name) && weekRegex.test(name)); //Filter overwritten and ignored cols by the user

        if(weekMatches.length > 0 || overwrittenWeeks.length > 0) {            
            const combinedWeeks = [...overwrittenWeeks.map(c => ({name: c.name, index: c.index})), ...weekMatches];

            detectedWeeks = {

                names: combinedWeeks.map(w => w.name),
                indexes: combinedWeeks.map(w => w.index),
                offsets: weekOffsets
            };
            // console.log("Detected weeks: ", detectedWeeks);
        }


        if (detectedMonths.names.length === 0 && detectedWeeks.names.length === 0) {
            const keywordMatches = colNames
                .map((col, i) => ({ name: col, index: i }))
                .filter(({ name }) => !userOverwrites[name] && !ignoredCols.has(name) && keywordRegex.test(name.toLowerCase()));

            const fallbackResult = {
                names: keywordMatches.map(m => m.name),
                indexes: keywordMatches.map(m => m.index),
                offsets: {}
            };
            detectedMonths = fallbackResult;
            // console.log("Detected by keyword fallback:", fallbackResult);
        }
        // Step 2: Fallback to keyword detection
        
        return {months: detectedMonths, weeks: detectedWeeks};

    };

    useEffect(() => {
        const runPredictions = async () => {
            setLoadingColumnIdentification(true);
            const newPredictions = {};
            const promises = [];
            //Iterates through every file
            for (const fileName of Object.keys(dataFrames)) {
                const data = dataFrames[fileName];
                if (!data) {
                    return;
                }

                for (const sheetName of Object.keys(data)) {
                    const tables = data[sheetName];
                    for (let i = 0; i < tables.length; i++) {
                        const table = tables[i];
                        const key = `${fileName}|${sheetName}|${i}`;
                        //Create a promise for each id-time prediction
                        const predictedPromises = Promise.all([predictIdColumn(table, key, overwrites, ignoredColumns), detectMonthQuantityColumns(table, key, overwrites, ignoredColumns)])
                        .then(([idPrediction, timePredictions]) => {
                            newPredictions[key] = {
                                id: idPrediction,
                                months: timePredictions.months,
                                weeks: timePredictions.weeks
                            };
                        });
                        promises.push(predictedPromises);
                    }
                }
            }

            //Waits untill every promise is resolved (every file is iterated)
            await Promise.all(promises);
            setAllPredictions(newPredictions);
            setLoadingColumnIdentification(false);
        };

        runPredictions();
    }, [dataFrames, overwrites, ignoredColumns]);

    //This is used to keep the navigation functions without changes, taking the current selected document
    useEffect(() => {
        const key = getContextKey();
        const predictions = allPredictions[key];

        if(predictions) {
            setPredictedIdColumn(predictions.id);
            setPredictedMonthColumns(predictions.months);
            setPredictedWeekColumns(predictions.weeks);
        }
    }, [allPredictions, getContextKey, table]);

    useEffect(() => {
        if (!allPredictions || Object.keys(allPredictions).length === 0) {
            props.detectMonths([]);
            return;
        }

        const allDetectedMonths = new Set();
        const currentYear = new Date().getFullYear();

        //Iterates over all predictions to get all the months
        for (const key in allPredictions) {
            const predictions = allPredictions[key];
            if (!predictions) continue;

            const {months, weeks} = predictions;

            if(months && months.names) {
                months.names.forEach(monthName => {
                    const normalizeMonth = normalizeMonthName(monthName);
                    // console.log("NormalizedMonth: ", normalizeMonth);
                    const offset = months.offsets[monthName] || 0;
                    const year = currentYear + offset;
                    allDetectedMonths.add(`${normalizeMonth} ${year}`);
                });
            }

            if(weeks && weeks.names) {
                const [fileName, sheetName, tableIndexStr] = key.split('|');
                const tableIndex = parseInt(tableIndexStr, 10);
                const table = dataFrames[fileName]?.[sheetName]?.[tableIndex];

                if(table) {
                    const monthsFromWeeks = transformWeeksToMonths(table, weeks.names, weeks.offsets)
                    monthsFromWeeks.forEach(month => allDetectedMonths.add(month));
                }
            }
        }

        // console.log("Detected months: ", allDetectedMonths);
        props.detectMonths(Array.from(allDetectedMonths));
    }, [allPredictions, dataFrames]);

    const renameColumn = (oldName, newName) => {
        if (!table) return;
        const newTables = [...tables];
        newTables[tableIndex] = newTables[tableIndex].map(row => {
            const newRow = new Map();
            for (const [key, value] of row.entries()) {
                newRow.set(key === oldName ? newName : key, value);
            }
            return newRow;
        });
    
        setDataFrames(prev => ({
            ...prev,
            [fileName]: {
                ...prev[fileName],
                [sheetName]: newTables,
            }
        }));
    };
    
    const markColumnAsValid = (colName) => {
        const key = getContextKey();
        setValidColumnsMap(prev => {
            const updated = { ...prev };
            const current = new Set(updated[key] || []);
        
            if (current.has(colName)) {
              current.delete(colName);  // Unmark if already present
            } else {
              current.add(colName);     // Mark if not present
            }
        
            updated[key] = Array.from(current);
            return updated;
          });
        // console.log(validColumnsMap)
    };

    // Check if a column is valid (user-marked or predicted)
    const isColumnValid = (colName, colIndex) => {
        const key = getContextKey();
        const manuallyMarked = validColumnsMap[key] || [];
        return manuallyMarked.includes(colName)
        || predictedIdColumn.name === colName
        || predictedMonthColumns.names.includes(colName)
        || predictedWeekColumns.names.includes(colName)
        || predictedIdColumn.index === colIndex
        || predictedMonthColumns.indexes.includes(colIndex)
        || predictedWeekColumns.indexes.includes(colIndex);
    };

    // Delete columns not marked as valid
    const deleteInvalidColumns = () => {
        if (!table) return;
        const colNames = Array.from(table[0].keys());
    
        const columnsToKeep = colNames.filter((colName, i) => isColumnValid(colName, i));
    
        const newTables = [...tables];
        newTables[tableIndex] = newTables[tableIndex].map(row => {
        const newRow = new Map();
        columnsToKeep.forEach(k => newRow.set(k, row.get(k)));
        return newRow;
        });
    
        setDataFrames(prev => ({
        ...prev,
        [fileName]: {
            ...prev[fileName],
            [sheetName]: newTables,
        }
        }));
    };

    const getInfoFromWeeks = (weekNumber, offset) => {  //Returns the month and year corresponding to a week.
        const monthNames = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
        const currentYear = new Date().getFullYear();
        const year = currentYear + offset;

        let date = new Date();
        date = setYear(date, year);
        date = setWeek(date, weekNumber, {locale: es});

        const monthIndex = date.getMonth();
        return `${monthNames[monthIndex]} ${year}`;
    }

    const transformWeeksToMonths = (table, weekCols, predictedWeekOffsets) => {
        const monthNames = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
        const transformedMonths = new Set();    //We use a set to avoid duplicates, as we are transforming from weeks.
        const currentYear = new Date().getFullYear();
        const headers = table ? Array.from(table[0].keys()) : [];

        weekCols.forEach(header => {
            const currentHeader = headers.find(h => h === header) || header;
            const weekNumber = parseInt(currentHeader.match(/\d+/)[0]);
            const offset = predictedWeekOffsets[header] || 0;
            const year = currentYear + offset;

            let date = new Date();
            date = setYear(date, year);
            date = setWeek(date, weekNumber, {locale: es});

            const monthIndex = date.getMonth();
            transformedMonths.add(`${monthNames[monthIndex]} ${year}`); //We add the year as it'll be needed in the SelectMonthContainer
        });

        return Array.from(transformedMonths);
    }

    const aggregateWeekData = (table, weekCols, idColName, weekOffsets) => {
        const aggregatedRows = [];

        for(const row of table) {   
            const aggregatedRow = new Map();
            const id = row.get(idColName);
            aggregatedRow.set(idColName, id);
            const monthSums = {};

            for(const header of weekCols) {
                const weekNumber = parseInt(header.match(/\d+/)[0]);
                const offset = weekOffsets[header] || 0;
                const monthYear = getInfoFromWeeks(weekNumber, offset);

                const value = row.get(header);  //Gets the current value

                const formattedValue = String(value ?? '').replace(/,/g, '').trim();  //Remove the commas from the value (Although the value should be a string already, parse it just in case...)
                const numValue = parseFloat(formattedValue);
                if (isNaN(numValue)) {
                    continue;   //Skip empty/non-numeric weeks, otherwise the NaN resets the month's sum
                }
                monthSums[monthYear] = (monthSums[monthYear] || 0) + numValue; //Adds the total value for the corresponding month
            }

            for (const [month, sum] of Object.entries(monthSums)) {
                aggregatedRow.set(month, sum);
            }

            aggregatedRows.push(aggregatedRow);
        }

        return aggregatedRows;  //Returns a table with the data of the weeks, already transformed in months (e.g. If a month has four weeks, the data from those four weeks for a specific NoParte will be added up)
    }
  
    const generateFPR = (selectedMonths) => {
        let allRows = [];

        //Iterates through every file
        for (const fileName of Object.keys(dataFrames)) {
            const data = dataFrames[fileName];

            for (const sheetName of Object.keys(data)) {
                const tables = data[sheetName];

                tables.forEach((table, tableIndex) => {
                    const contextKey = `${fileName}|${sheetName}|${tableIndex}`;
                    let source = table;

                    const predictions = allPredictions[contextKey];
                    if (!predictions) {
                        // console.log("No hay datos para esta llave: ", contextKey);
                        return;
                    }

                    if(predictions.weeks.names.length > 0) {
                        source = aggregateWeekData(table, predictions.weeks.names, predictions.id.name, predictions.weeks.offsets);
                    }

                    if (!source || source.length === 0) {
                        return;
                    } 

                    const idHeader = predictions.id.name;

                    source.forEach(row => {
                        const NoParte = row.get(idHeader);
                        selectedMonths.forEach(month => {
                            const monthName = month.match(/[a-z]+/i)[0];
                            const year = parseInt(month.match(/\d+/)[0]);
                            const monthIndex = monthMap[monthName.toLowerCase()];

                            const fecha = new Date(year, monthIndex, 1).toISOString().split('T')[0];    //Calculates the date of the first day of the month
                            let cantidad;
                            if(predictions.months.names.length > 0) {
                                const matchingMonth = predictions.months.names.find(h => {
                                    const normalizedHeader = normalizeMonthName(h);
                                    const headerYear = new Date().getFullYear() + (predictions.months.offsets[h] || 0);   //Same year calculation used for the detected months
                                    return normalizedHeader.toLowerCase() === monthName.toLowerCase() && headerYear === year;
                                })
                                // console.log("Matching: ", matchingMonth);
                                cantidad = matchingMonth ? row.get(matchingMonth) : null;
                                // console.log("Cantidad: ", cantidad);
                            } else {
                                cantidad = row.get(month);
                            }

                            const formattedCantidad = String(cantidad ?? '').replace(/,/g, '').trim();
                            const final = parseFloat(formattedCantidad);
                            // console.log("Final: ", final);

                            if(!isNaN(final) && final > 0) {
                                allRows.push({NoParte, fecha, cantidad: final});
                            }
                        })
                    })
                })
            }
        }

        // console.log("All rows: ", allRows);
        setFprData(allRows);
        // console.log("Final FPR table:", allRows);
    };

    useEffect(() => {
        // console.log("Fpr data: ", fprData);
        if(fprData && fprData.length > 0) {
            // console.log(1);
            generateForecast();
        }
    }, [fprData]);

    const generateForecast = async () => {
        try {   
            props.generateReport(true);
            props.addProgress(50);
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            const response = await fetch(`${process.env.REACT_APP_FORECAST_SEND_URL}`, {mode:'cors', headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },method:'POST', body: JSON.stringify(fprData)});
            const resData = await response.json();
            // console.log(resData);
        } catch (e) {
            console.error(e)
        } finally {
            props.generateReport(false);
            props.addProgress(100);
        }
    }

    useEffect(() => {
        if (props.progress === 100) {
            props.addProgress(0);
            props.redirectToChart(); 
        }
    }, [props.progress]);

    useImperativeHandle(ref, () => ({
        generateFPR: generateFPR
    }));

    return (
        <Col style={{flex: isMobile ? 'none' : 3, width: isMobile ? '100%' : undefined, alignItems:'flex-start', paddingLeft: isMobile ? 0 : 32}}>
            {/* File Navigation */}
            <Row style={{alignItems: 'center', justifyContent: 'flex-start', marginBottom: 20, width: '100%'}}>
                <button style={{background: 'transparent', border: 0, paddingRight: 16, flex: 'none'}} onClick={() => changeFile(-1)} disabled={fileIndex === 0 || props.progress}><ArrowBackIosIcon style={{width: 14, height: 18, color: fileIndex === 0 || props.progress ? props.theme.Coco : props.theme.SEVENB}}/></button>

                <Row key={fileName} style={{border:'solid 1px', borderColor: props.theme.Ey, borderRadius:4, padding:16, alignItems:'center', width: isMobile ? '100%' : 552, minWidth: 0, flex: isMobile ? 1 : 'none'}}>
                    <Col style={{alignItems:'flex-start', gap: 8, minWidth: 0, flex: 1}}>
                        <Text color={props.theme.SEVENB} size={17} weight={"medium"} style={{textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: isMobile ? '100%' : 400}}>{fileName}</Text>
                    </Col>
                    <Col style={{flex:'none', height:'100%'}}>
                        <RemoveCircleIcon onClick={() => deleteFile(fileName)} disabled={fileNames.length === 0 || props.progress} style={{color:props.theme.Red, fontSize: 24, cursor:'pointer'}}/>
                    </Col>
                </Row>
                <button style={{background: 'transparent', border: 0, paddingLeft: 16, flex: 'none'}} onClick={() => changeFile(1)} disabled={fileIndex === fileNames.length - 1 || props.progress}><ArrowForwardIosIcon style={{width: 14, height: 18, color: fileIndex === fileNames.length - 1 || props.progress ? props.theme.Coco : props.theme.SEVENB}}/></button>
            </Row>
            <Row style={{flexWrap: 'nowrap', gap: isMobile ? 12 : 0}}>
                {/* Sheet Navigation */}
                <Col style={{alignItems: 'flex-start', marginBottom: 20}}>
                    <Text size={isMobile ? 15 : 17} color={props.theme.ONED}>Página: {sheetName}</Text>
                    <Row style={{width: isMobile ? 150 : 312, justifyContent:'start', gap: 10}}>
                        <button style={{background: 'transparent', border: 0}} onClick={() => changeSheet(-1)} disabled={sheetIndex === 0 || props.progress}><ArrowBackIosIcon style={{width: 14, height: 18, color: sheetIndex === 0 || props.progress ? props.theme.Coco : props.theme.SEVENB}}/></button>
                        <Text color={props.theme.SEVENB}>{sheetIndex + 1} / {sheetNames.length}</Text>
                        <button style={{background: 'transparent', border: 0, paddingRight: 16}} onClick={() => changeSheet(1)} disabled={sheetIndex === sheetNames.length - 1 || props.progress}><ArrowForwardIosIcon style={{width: 14, height: 18, color: sheetIndex === sheetNames.length - 1 || props.progress ? props.theme.Coco : props.theme.SEVENB}}/></button>
                        <RemoveCircleIcon onClick={() => deleteSheet(fileName, sheetName)} disabled={sheetNames.length === 0 || props.progress} style={{color:props.theme.Red, fontSize: 24, cursor:'pointer'}}/>
                    </Row>
                </Col>
                {/* Table Navigation */}
                <Col style={{alignItems: 'flex-start', marginBottom: 20}}>
                    <Text size={isMobile ? 15 : 17} color={props.theme.ONED}>Tabla</Text>
                    <Row style={{width: isMobile ? 150 : 312, justifyContent:'start', gap: 10}}>
                        <button style={{background: 'transparent', border: 0}} onClick={() => changeTable(-1)} disabled={tableIndex === 0 || props.progress}><ArrowBackIosIcon style={{width: 14, height: 18, color: tableIndex === 0 || props.progress ? props.theme.Coco : props.theme.SEVENB}}/></button>
                        <Text color={props.theme.SEVENB}>{tableIndex + 1} / {tables.length}</Text>
                        <button style={{background: 'transparent', border: 0, paddingRight: 16}} onClick={() => changeTable(1)} disabled={tableIndex === tables.length - 1 || props.progress}><ArrowForwardIosIcon style={{width: 14, height: 18, color: tableIndex === tables.length - 1 || props.progress ? props.theme.Coco : props.theme.SEVENB}}/></button>
                        <RemoveCircleIcon onClick={() => deleteTable(fileName, sheetName, tableIndex)} disabled={tables.length === 0 || props.progress} style={{color:props.theme.Red, fontSize: 24, cursor:'pointer'}}/>
                    </Row>
                </Col>
            </Row>
            <Row style={{ marginBottom: 20, marginTop: 10 }}>
                <Text color={props.theme.SEVENB} size={isMobile ? 14 : 17}>Selecciona las columnas que incluyan la información necesaria para hacer el forecast: Numero de parte, Fechas por mes (Ene, Enero, Jan) ó por semana (WK22, 22)</Text>
            </Row>

            <Row style={{ marginBottom: 30, marginTop: 10 }}>
                <Text disabled={props.progress} size={isMobile ? 14 : 17} style={{cursor:'pointer'}} onClick={deleteInvalidColumns} color={props.theme.Red}>Eliminar columnas no utilizadas</Text>
            </Row>
            
            {loadingColumnIdentification && (
                <Row style={{justifyContent: 'space-between', alignItems: 'start', width: isMobile ? '100%' : '35%', gap: 12}}>
                    <Text weight="bold" size={isMobile ? 13 : 16} color={props.theme.ONED} style={{marginBottom: 5, whiteSpace: 'nowrap'}}> Identificando columnas...</Text>
                    <Col style={{backgroundColor: props.theme.White, flex:'none', width: '50%', height: 6 , borderRadius: 10, marginTop: 8}}>
                        <Col className={'div-animate'} style={{width:`${100}%`, borderRadius:10}} ></Col>
                    </Col>
                </Row>
            )} 
            {/* Table Display */}
            {table && Array.isArray(table) && table.length > 0 && (
                <ForecastTable 
                deleteHeaderRow={deleteHeaderRow} 
                deleteColumn={deleteColumn} 
                deleteRow={deleteRow} 
                markColumnAsValid={markColumnAsValid}
                renameColumn={renameColumn}
                table={table}
                predictedIdColumn={predictedIdColumn}
                predictedMonthColumns={predictedMonthColumns}
                predictedWeekColumns={predictedWeekColumns}
                overwrite={handleOverwrite}
                deselect={handleDeselection}
                theme={props.theme}
                message={message}
                validColumns={validColumnsMap[`${fileName}|${sheetName}|${tableIndex}`] || []}
                />
            )}
            {!props.confirmAction[1] && (
                <>
                    <Row style={{ marginBottom: 20, marginTop: 40, width: isMobile ? '100%' : undefined }}>
                        <Text color={props.theme.SEVENB} size={isMobile ? 14 : 17}>Asegúrate de revisar todos los archivos antes de confirmar las tablas.</Text>
                    </Row>
                    <Row style={{ marginBottom: 20, width: isMobile ? '100%' : undefined }}>
                        <Button variant="contained" onClick={() => props.handleConfirm(1)} sx={{width: isMobile ? '70%' : 342, maxWidth: 342, height: 52, backgroundColor: props.theme.ONED, color: props.theme.White, fontWeight: 'medium', fontSize: 15, textTransform: 'none'}}>Confirmar tablas</Button>
                    </Row>
                </>
            )}
        </Col>
    )
});

export default ForecatNormalizer;